// stores/wsStore.js
import { Nullable } from '@crossbeam/types';

import { useWebSocket } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import appConfig from '@/config';

export type ChatErrorCode = 'unknown' | 'rate_limit' | 'context_overflow';

export type Message = {
  content: string;
  chatSessionId: string;
  messageId?: string;
  messageComplete: boolean;
  isFirst: boolean;
  errorCode: Nullable<ChatErrorCode>;
};

export type SentMessage = {
  content: string;
  current_page: {
    url: string;
    params: Record<string, string>;
  } | null;
  context?: string | null;
  type: 'message';
};

export const useWebSocketStore = defineStore('wsStore', () => {
  const messages = ref<string[]>([]);
  const connectionFailed = ref(false);
  const parsedMessages = computed<Message[]>(() => {
    return messages.value.map((msg) => JSON.parse(msg));
  });
  const chatSessionId = computed(() => {
    // we want to use the last message's chatSessionId in case we start a fresh one
    // due to context limit errors.
    return (
      parsedMessages?.value[parsedMessages?.value.length - 1]?.chatSessionId ||
      null
    );
  });

  function getUrl() {
    const { currentOrg } = useAuth();
    const orgId = currentOrg.value?.uuid;
    if (!orgId) return '';
    let url = `wss://${appConfig.aiChatHost}/ws/?organization_uuid=${orgId}`;
    if (chatSessionId.value) {
      url += `&chat_session_id=${chatSessionId.value}`;
    }
    return url;
  }
  const url = ref<string>(getUrl());

  const { status, open, send, close } = useWebSocket(url, {
    onConnected() {
      connectionFailed.value = false;
    },
    immediate: false,
    onMessage(_ws, event) {
      messages.value.push(event.data);
    },
    autoReconnect: {
      retries: 30,
      delay: 5000,
      onFailed() {
        connectionFailed.value = true;
      },
    },
    onDisconnected() {
      url.value = getUrl();
    },
  });

  const isConnected = computed(() => {
    return status.value === 'OPEN';
  });

  function connect() {
    if (isConnected.value) return;
    connectionFailed.value = false;
    url.value = getUrl();
    open();
  }

  function sendMessage(data: SentMessage | { type: 'cancel' }) {
    if (status.value === 'OPEN') {
      send(JSON.stringify(data));
    }
  }

  function getMessagesById(id: string) {
    return parsedMessages.value.filter((msg) => msg.messageId === id);
  }

  return {
    parsedMessages,
    isConnected,
    getMessagesById,
    connect,
    sendMessage,
    disconnect: close,
    chatSessionId,
    connectionFailed,
  };
});
